/**
 * FlowNodes — Custom React Flow node types for the Visual Agent Builder
 *
 * Node types: Start, Agent, Tool, DataSource, Output, Condition
 * All follow OLED Black & Gold theme from design-system constants.
 */

import React, { memo } from 'react';
import { Handle, Position } from '@xyflow/react';
import {
  Play, Brain, Wrench, Database, FileText, GitBranch,
  Target, Radio, Eye, Video, Watch, Clipboard, DollarSign,
  Users, BarChart3, Scissors, Search, Calculator, Monitor,
  MessageSquare, Mail, FileDown, Globe, BookOpen, Upload,
  Circle, Grid, Heart, Plug, Film, Smartphone, Image, Lock,
} from 'lucide-react';

const ICON_MAP = {
  Play, Brain, Wrench, Database, FileText, GitBranch,
  Target, Radio, Eye, Video, Watch, Clipboard, DollarSign,
  Users, BarChart3, Scissors, Search, Calculator, Monitor,
  MessageSquare, Mail, FileDown, Globe, BookOpen, Upload,
  Circle, Grid, Heart, Plug, Film, Smartphone, Image, Lock,
};

function getIcon(name, fallback = Circle) {
  return ICON_MAP[name] || fallback;
}

// Shared handle styles
const handleStyle = {
  width: 8,
  height: 8,
  background: '#E8B800',
  border: '2px solid #000',
};

const sourceHandleStyle = { ...handleStyle, right: -4 };
const targetHandleStyle = { ...handleStyle, left: -4 };

// Node type color map
const NODE_COLORS = {
  start:      { bg: '#0A2A0A', border: '#00CC66', accent: '#00CC66', icon: '#00CC66' },
  agent:      { bg: '#1A1400', border: '#E8B800', accent: '#E8B800', icon: '#E8B800' },
  tool:       { bg: '#0A0A1A', border: '#0088FF', accent: '#0088FF', icon: '#0088FF' },
  dataSource: { bg: '#0A1A0A', border: '#00ff88', accent: '#00ff88', icon: '#00ff88' },
  output:     { bg: '#1A0A1A', border: '#CC66FF', accent: '#CC66FF', icon: '#CC66FF' },
  condition:  { bg: '#1A1A0A', border: '#FF9500', accent: '#FF9500', icon: '#FF9500' },
};

// Base node wrapper
function NodeShell({ children, colors, selected, label, icon: IconName, hasSource = true, hasTarget = true }) {
  const Icon = typeof IconName === 'string' ? getIcon(IconName) : IconName;

  return (
    <div
      className="rounded-xl min-w-[180px] max-w-[220px] transition-all"
      style={{
        background: colors.bg,
        border: `1.5px solid ${selected ? colors.accent : colors.border + '60'}`,
        boxShadow: selected ? `0 0 16px ${colors.accent}30` : 'none',
      }}
    >
      {hasTarget && (
        <Handle type="target" position={Position.Left} style={targetHandleStyle} />
      )}

      {/* Header */}
      <div
        className="flex items-center gap-2 px-3 py-2 rounded-t-xl"
        style={{ borderBottom: `1px solid ${colors.border}30` }}
      >
        <div
          className="w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0"
          style={{ background: colors.accent + '20' }}
        >
          <Icon size={14} style={{ color: colors.icon }} />
        </div>
        <span className="text-xs font-medium truncate" style={{ color: '#F5F5F5' }}>
          {label}
        </span>
      </div>

      {/* Body */}
      <div className="px-3 py-2">
        {children}
      </div>

      {hasSource && (
        <Handle type="source" position={Position.Right} style={sourceHandleStyle} />
      )}
    </div>
  );
}

// ─── Start Node ──────────────────────────────────────────────────────────────
export const StartNode = memo(({ data, selected }) => (
  <NodeShell
    colors={NODE_COLORS.start}
    selected={selected}
    label={data.label || 'Start'}
    icon={Play}
    hasTarget={false}
  >
    <p className="text-[10px]" style={{ color: '#999' }}>
      {data.triggerType || 'Manual trigger'}
    </p>
  </NodeShell>
));

// ─── Agent Node ──────────────────────────────────────────────────────────────
export const AgentNode = memo(({ data, selected }) => (
  <NodeShell
    colors={NODE_COLORS.agent}
    selected={selected}
    label={data.label || 'AI Agent'}
    icon={Brain}
  >
    <p className="text-[10px] leading-relaxed line-clamp-2" style={{ color: '#999' }}>
      {data.description || 'Configure agent instructions'}
    </p>
    {data.model && (
      <div className="mt-1.5">
        <span
          className="text-[9px] px-1.5 py-0.5 rounded"
          style={{ background: '#E8B80015', color: '#E8B800', border: '1px solid #E8B80030' }}
        >
          {data.model}
        </span>
      </div>
    )}
  </NodeShell>
));

// ─── Tool Node ───────────────────────────────────────────────────────────────
export const ToolNode = memo(({ data, selected }) => (
  <NodeShell
    colors={NODE_COLORS.tool}
    selected={selected}
    label={data.label || 'Tool'}
    icon={data.icon || 'Wrench'}
  >
    <p className="text-[10px] leading-relaxed line-clamp-2" style={{ color: '#999' }}>
      {data.description || 'Select a tool'}
    </p>
    {data.category && (
      <div className="mt-1.5">
        <span
          className="text-[9px] px-1.5 py-0.5 rounded"
          style={{ background: '#0088FF15', color: '#0088FF', border: '1px solid #0088FF30' }}
        >
          {data.category}
        </span>
      </div>
    )}
  </NodeShell>
));

// ─── Data Source Node ────────────────────────────────────────────────────────
export const DataSourceNode = memo(({ data, selected }) => (
  <NodeShell
    colors={NODE_COLORS.dataSource}
    selected={selected}
    label={data.label || 'Data Source'}
    icon={data.icon || 'Database'}
    hasTarget={false}
  >
    <p className="text-[10px] leading-relaxed line-clamp-2" style={{ color: '#999' }}>
      {data.description || 'Select a data source'}
    </p>
    {!data.available && (
      <div className="mt-1.5 flex items-center gap-1">
        <Lock size={9} style={{ color: '#555' }} />
        <span className="text-[9px]" style={{ color: '#555' }}>Not connected</span>
      </div>
    )}
  </NodeShell>
));

// ─── Output Node ─────────────────────────────────────────────────────────────
export const OutputNode = memo(({ data, selected }) => (
  <NodeShell
    colors={NODE_COLORS.output}
    selected={selected}
    label={data.label || 'Output'}
    icon={data.icon || 'FileText'}
    hasSource={false}
  >
    <p className="text-[10px] leading-relaxed line-clamp-2" style={{ color: '#999' }}>
      {data.description || 'Configure output format'}
    </p>
  </NodeShell>
));

// ─── Condition Node ──────────────────────────────────────────────────────────
export const ConditionNode = memo(({ data, selected }) => (
  <NodeShell
    colors={NODE_COLORS.condition}
    selected={selected}
    label={data.label || 'Condition'}
    icon={GitBranch}
  >
    <p className="text-[10px] leading-relaxed line-clamp-2" style={{ color: '#999' }}>
      {data.condition || 'Set branching logic'}
    </p>
  </NodeShell>
));

StartNode.displayName = 'StartNode';
AgentNode.displayName = 'AgentNode';
ToolNode.displayName = 'ToolNode';
DataSourceNode.displayName = 'DataSourceNode';
OutputNode.displayName = 'OutputNode';
ConditionNode.displayName = 'ConditionNode';

// ─── Node Types Map (for React Flow) ─────────────────────────────────────────
export const nodeTypes = {
  start: StartNode,
  agent: AgentNode,
  tool: ToolNode,
  dataSource: DataSourceNode,
  output: OutputNode,
  condition: ConditionNode,
};

// ─── Default Nodes for Empty Canvas ──────────────────────────────────────────
export const DEFAULT_NODES = [
  {
    id: 'start-1',
    type: 'start',
    position: { x: 50, y: 200 },
    data: { label: 'Start', triggerType: 'Manual trigger' },
  },
  {
    id: 'agent-1',
    type: 'agent',
    position: { x: 350, y: 180 },
    data: { label: 'My Agent', description: 'Agent to help interact with my data', model: 'Gemini 3 Flash' },
  },
];

export const DEFAULT_EDGES = [
  {
    id: 'e-start-agent',
    source: 'start-1',
    target: 'agent-1',
    type: 'smoothstep',
    animated: true,
    style: { stroke: '#E8B80060', strokeWidth: 2 },
  },
];

export { NODE_COLORS };
